import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailService = () => {
  const { user } = useAuth();
  
  // Email service state
  const [emailSettings, setEmailSettings] = useState({
    provider_name: '',
    api_id: '',
    api_key_set: false,
    sender_email: '',
    sender_name: '',
    is_enabled: false,
    last_tested_at: null,
    last_test_success: null,
    last_error: null
  });
  
  // Form state
  const [formData, setFormData] = useState({
    provider_name: '',
    api_id: '',
    api_key: '',
    sender_email: '',
    sender_name: '',
    is_enabled: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);

  const fetchDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const response = await axios.get(`${API}/admin/integrations/email/diagnostics?user_id=${user.id}`);
      setDiagnostics(response.data);
    } catch (err) {
      console.error('Error fetching diagnostics:', err);
      setError('Failed to load diagnostics');
    }
    setLoadingDiagnostics(false);
  };

  const fetchEmailSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/integrations/email?user_id=${user.id}`);
      setEmailSettings(response.data);
      setFormData({
        provider_name: response.data.provider_name || '',
        api_id: response.data.api_id || '',
        api_key: '',
        sender_email: response.data.sender_email || '',
        sender_name: response.data.sender_name || '',
        is_enabled: response.data.is_enabled || false
      });
    } catch (err) {
      console.error('Error fetching email settings:', err);
      // If 404, that's okay - means no settings exist yet
      if (err.response?.status !== 404) {
        setError('Failed to load email service settings');
      }
    }
    setLoading(false);
  }, [user.id]);

  useEffect(() => {
    fetchEmailSettings();
  }, [fetchEmailSettings]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateApiKey = (apiKey) => {
    if (!apiKey) return { valid: true, message: '' }; // Empty is valid (means not updating)
    if (!apiKey.trim().startsWith('SG.')) {
      return { valid: false, message: 'SendGrid API key must start with "SG."' };
    }
    if (apiKey.trim().length < 50) {
      return { valid: false, message: 'API key seems too short. Please verify you copied the complete key.' };
    }
    return { valid: true, message: '' };
  };

  const handleSave = async () => {
    // Validation
    if (!formData.provider_name.trim()) {
      setError('Email Provider Name is required');
      return;
    }
    if (!formData.sender_email.trim()) {
      setError('Sender Email Address is required');
      return;
    }
    if (!emailSettings.api_key_set && !formData.api_key) {
      setError('API Key is required for new configuration');
      return;
    }
    
    // Validate API key format
    const apiKeyValidation = validateApiKey(formData.api_key);
    if (!apiKeyValidation.valid) {
      setError(apiKeyValidation.message);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        provider_name: formData.provider_name,
        api_id: formData.api_id,
        sender_email: formData.sender_email,
        sender_name: formData.sender_name,
        is_enabled: formData.is_enabled
      };
      
      // Only include API key if it was changed
      if (formData.api_key) {
        updateData.api_key = formData.api_key;
      }

      await axios.put(`${API}/admin/integrations/email?user_id=${user.id}`, updateData);
      
      setSuccess('Email service settings saved successfully');
      setFormData(prev => ({ ...prev, api_key: '' }));
      setShowApiKeyInput(false);
      fetchEmailSettings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress.trim()) {
      setError('Please enter a test email address');
      return;
    }

    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API}/admin/integrations/email/test?user_id=${user.id}`,
        { test_email_address: testEmailAddress }
      );
      
      if (response.data.success) {
        setSuccess(response.data.message || 'Test email sent successfully');
      } else {
        setError(response.data.error || 'Test failed');
      }
      fetchEmailSettings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send test email');
    }
    setTesting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm">
          <li>
            <span className="text-gray-500">Settings</span>
          </li>
          <li>
            <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </li>
          <li>
            <span className="text-gray-500">Integration Settings</span>
          </li>
          <li>
            <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </li>
          <li>
            <span className="text-red-600 font-medium">Email Service</span>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Email Service Configuration</h1>
        <p className="mt-2 text-gray-600">Configure your email provider for sending transactional emails</p>
      </div>

      {/* Not Configured Warning */}
      {!emailSettings.api_key_set && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-amber-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-amber-800 font-medium">Email Service Not Configured</h3>
              <p className="text-amber-700 text-sm mt-1">
                Email notifications are currently disabled. Configure your email provider below to enable transactional emails.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-700">{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main Configuration Card */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="h-6 w-6 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            Email Provider Configuration
          </h2>
          <p className="text-sm text-gray-500 mt-1">Configure any email service provider (SendGrid, SES, Zoho, Outlook, etc.)</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Provider Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Provider Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="provider_name"
              value={formData.provider_name}
              onChange={handleInputChange}
              placeholder="e.g., SendGrid, Amazon SES, Zoho, Outlook"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <p className="text-xs text-gray-500 mt-1">Enter the name of your email service provider</p>
          </div>

          {/* API ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API ID
            </label>
            <input
              type="text"
              name="api_id"
              value={formData.api_id}
              onChange={handleInputChange}
              placeholder="API Username, Client ID, or Account ID (if applicable)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <p className="text-xs text-gray-500 mt-1">Some providers require an API ID in addition to the API key</p>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key <span className="text-red-500">*</span>
            </label>
            {emailSettings.api_key_set && !showApiKeyInput ? (
              <div className="flex items-center space-x-3">
                <div className="flex-1 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 font-mono">
                  ••••••••••••••••••••••••
                </div>
                <button
                  type="button"
                  onClick={() => setShowApiKeyInput(true)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Replace Key
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="password"
                  name="api_key"
                  value={formData.api_key}
                  onChange={handleInputChange}
                  placeholder="Enter your API key"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                {showApiKeyInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowApiKeyInput(false);
                      setFormData(prev => ({ ...prev, api_key: '' }));
                    }}
                    className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              <svg className="inline w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              API key is encrypted and never displayed after saving
            </p>
            {formData.provider_name.toLowerCase().includes('sendgrid') && (
              <p className="text-xs text-sky-600 mt-1">
                💡 SendGrid API keys start with "SG." - Make sure you copy the complete key from your SendGrid dashboard.
              </p>
            )}
          </div>

          {/* Sender Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sender Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="sender_email"
              value={formData.sender_email}
              onChange={handleInputChange}
              placeholder="noreply@yourdomain.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <p className="text-xs text-gray-500 mt-1">The email address that will appear as the sender</p>
          </div>

          {/* Sender Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sender Name
            </label>
            <input
              type="text"
              name="sender_name"
              value={formData.sender_name}
              onChange={handleInputChange}
              placeholder="Exlva DARe"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <p className="text-xs text-gray-500 mt-1">The display name that will appear as the sender (optional)</p>
          </div>

          {/* Integration Status Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Integration Status</h4>
              <p className="text-sm text-gray-500">Enable or disable email sending</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_enabled"
                checked={formData.is_enabled}
                onChange={handleInputChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                {formData.is_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Test Email Card */}
      {emailSettings.api_key_set && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <svg className="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Test Email Configuration
            </h2>
          </div>

          <div className="p-6">
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Email Address
                </label>
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="test@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <button
                onClick={handleTestEmail}
                disabled={testing || !emailSettings.is_enabled}
                className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 transition-colors flex items-center"
              >
                {testing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                    </svg>
                    Send Test Email
                  </>
                )}
              </button>
            </div>

            {!emailSettings.is_enabled && (
              <p className="text-amber-600 text-sm mt-2">
                <svg className="inline w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Enable the integration to send test emails
              </p>
            )}

            {/* Last Test Status */}
            {emailSettings.last_tested_at && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Last Test Result</h4>
                <div className="flex items-center space-x-4 text-sm">
                  <span className={`flex items-center ${emailSettings.last_test_success ? 'text-green-600' : 'text-red-600'}`}>
                    {emailSettings.last_test_success ? (
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                    {emailSettings.last_test_success ? 'Success' : 'Failed'}
                  </span>
                  <span className="text-gray-500">
                    {formatDate(emailSettings.last_tested_at)}
                  </span>
                </div>
                {emailSettings.last_error && !emailSettings.last_test_success && (
                  <p className="text-red-600 text-sm mt-2">{emailSettings.last_error}</p>
                )}
              </div>
            )}

            {/* Troubleshoot Button */}
            {emailSettings.last_test_success === false && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={fetchDiagnostics}
                  disabled={loadingDiagnostics}
                  className="px-4 py-2 bg-amber-100 text-amber-800 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors flex items-center"
                >
                  {loadingDiagnostics ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-800 mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Troubleshoot Configuration
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Diagnostics Panel */}
            {diagnostics && (
              <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Configuration Diagnostics
                  </h4>
                  <button 
                    onClick={() => setDiagnostics(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Provider:</span>
                    <span className="font-medium">{diagnostics.provider_name || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Sender Email:</span>
                    <span className="font-medium">{diagnostics.sender_email || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">API Key Set:</span>
                    <span className={`font-medium ${diagnostics.api_key_set ? 'text-green-600' : 'text-red-600'}`}>
                      {diagnostics.api_key_set ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">API Key Format Valid:</span>
                    <span className={`font-medium ${diagnostics.api_key_valid_format ? 'text-green-600' : 'text-red-600'}`}>
                      {diagnostics.api_key_valid_format ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">API Key Preview:</span>
                    <span className="font-mono text-xs">{diagnostics.api_key_preview}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Enabled:</span>
                    <span className={`font-medium ${diagnostics.is_enabled ? 'text-green-600' : 'text-amber-600'}`}>
                      {diagnostics.is_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                {diagnostics.recommendations && diagnostics.recommendations.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <h5 className="font-medium text-slate-900 mb-2">Recommendations</h5>
                    <ul className="space-y-1">
                      {diagnostics.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-sm">{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-sky-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-sky-800">
            <h4 className="font-medium mb-1">Supported Email Providers</h4>
            <p>This configuration supports any email provider with an HTTP API, including:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sky-700">
              <li>SendGrid</li>
              <li>Amazon SES</li>
              <li>Mailgun</li>
              <li>Postmark</li>
              <li>Zoho Mail</li>
              <li>Microsoft Outlook/365</li>
              <li>And many more...</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailService;
